import { extractE2eeKey } from './e2ee-invitation.js';

const SESSION_KEY = 'vorbello_meeting_license_session';
const INVITE_KEY = 'vorbello_secure_meeting_invitation';
const ROOM_INVITE_KEY = 'vorbello_secure_room_invitation';
export class GuestWaitingForHost extends Error {
  constructor(public readonly startsAt?: string) { super('În așteptarea gazdei.'); }
}
export interface WaitingTicket {id:string;secret:string}
export class GuestWaitingApproval extends Error {
  constructor(public readonly ticket:WaitingTicket, public readonly startsAt?: string) { super('În așteptarea aprobării gazdei.'); }
}
function countdownStart(data: any): string | undefined {
  const start = data?.preEvent?.startsAt;
  return typeof start === 'string' && start.length <= 40 && Number.isFinite(Date.parse(start)) ? start : undefined;
}
function parseWaitingTicket(data:any):WaitingTicket {
  if (!data?.ticket || typeof data.ticket.id!=='string' || !/^[a-f0-9]{32}$/.test(data.ticket.id) ||
      typeof data.ticket.secret!=='string' || !/^[A-Za-z0-9_-]{43}$/.test(data.ticket.secret)) throw new Error('Cerere de acces invalidă.');
  return {id:data.ticket.id,secret:data.ticket.secret};
}

export interface SecureMeetingInvitation { meetingId: string; token: string; encryptionKey?: Uint8Array }
export interface SecureRoomInvitation { roomId: string; token: string; encryptionKey?: Uint8Array }
function storedKey(value: unknown): Uint8Array | undefined {
  if (value === undefined) return undefined;
  if (!Array.isArray(value) || value.length !== 32 || !value.every(n => Number.isInteger(n) && n >= 0 && n <= 255)) throw new Error('Cheia invitației este invalidă.');
  return Uint8Array.from(value);
}

export function captureSecureMeetingInvitation(input: string, storage: Pick<Storage, 'setItem'>, replaceUrl: (url: string) => void): SecureMeetingInvitation | null {
  const url = new URL(input);
  const params = new URLSearchParams(url.hash.slice(1));
  if (!params.has('invitation')) return null;
  if (url.searchParams.has('room')) return null;
  if (url.protocol !== 'https:' || url.hostname !== 'app.vorbello.eu' || url.username || url.password || (url.port && url.port !== '443')) throw new Error('Invitația nu provine din aplicația oficială.');
  const meetingId = url.searchParams.get('meeting') || '';
  const token = params.get('invitation') || '';
  if (!/^meet-[a-f0-9]{32}$/.test(meetingId) || !/^[A-Za-z0-9_-]{43}$/.test(token)) throw new Error('Invitație invalidă.');
  const encryptionKey = extractE2eeKey(input).key || undefined;
  const invitation: SecureMeetingInvitation = { meetingId, token, ...(encryptionKey ? { encryptionKey } : {}) };
  storage.setItem(INVITE_KEY, JSON.stringify({ meetingId, token, ...(encryptionKey ? { encryptionKey: Array.from(encryptionKey) } : {}) }));
  url.hash = '';
  replaceUrl(url.toString());
  return invitation;
}

export function captureSecureRoomInvitation(input: string, storage: Pick<Storage,'setItem'>, replaceUrl: (url:string)=>void): SecureRoomInvitation|null {
  const url=new URL(input), params=new URLSearchParams(url.hash.slice(1));
  if (!params.has('invitation') || !url.searchParams.has('room')) return null;
  if (url.protocol!=='https:' || url.hostname!=='app.vorbello.eu' || url.username || url.password || (url.port && url.port!=='443')) throw new Error('Invitație invalidă.');
  const roomId=url.searchParams.get('room')||'', token=params.get('invitation')||'';
  if (!/^room-[a-f0-9]{32}$/.test(roomId) || !/^[A-Za-z0-9_-]{43}$/.test(token)) throw new Error('Invitație invalidă.');
  const encryptionKey=extractE2eeKey(input).key || undefined;
  storage.setItem(ROOM_INVITE_KEY,JSON.stringify({roomId,token,...(encryptionKey ? {encryptionKey:Array.from(encryptionKey)} : {})})); url.hash=''; replaceUrl(url.toString());
  return {roomId,token,...(encryptionKey ? {encryptionKey} : {})};
}
export function restoreSecureRoomInvitation(storage: Pick<Storage,'getItem'>): SecureRoomInvitation|null {
  try { const saved=JSON.parse(storage.getItem(ROOM_INVITE_KEY)||'null'); if (!saved || !/^room-[a-f0-9]{32}$/.test(saved.roomId) || !/^[A-Za-z0-9_-]{43}$/.test(saved.token)) return null; const encryptionKey=storedKey(saved.encryptionKey); return {roomId:saved.roomId,token:saved.token,...(encryptionKey ? {encryptionKey} : {})}; } catch (_) { return null; }
}
export function roomInvitationUrl(roomId:string,token:string):string {
  if (!/^room-[a-f0-9]{32}$/.test(roomId)||!/^[A-Za-z0-9_-]{43}$/.test(token)) throw new Error('Invitație invalidă.');
  const url=new URL('https://app.vorbello.eu/'); url.searchParams.set('room',roomId); url.hash=new URLSearchParams({invitation:token}).toString(); return url.toString();
}
export async function requestRoomInvitation(action:'get'|'join',roomId:string,token='',displayName='',transport:typeof fetch=fetch,pin='',ticket?:WaitingTicket):Promise<Record<string,unknown>> {
  if (!/^room-[a-f0-9]{32}$/.test(roomId)) throw new Error('Sală invalidă.');
  const headers:Record<string,string>={'Content-Type':'application/json'};
  if(action==='get') { const session=sessionStorage.getItem(SESSION_KEY); if(!session) throw new Error('Autentificare necesară.'); headers.Authorization=`Bearer ${session}`; }
  else if(!/^[A-Za-z0-9_-]{43}$/.test(token)||!displayName.trim()||displayName.length>160) throw new Error('Invitație invalidă.');
  const response=await transport('https://app.vorbello.eu/room-invites.php',{method:'POST',headers,credentials:'omit',cache:'no-store',referrerPolicy:'no-referrer',body:JSON.stringify({action,roomId,token,displayName,pin,waitingId:ticket?.id||'',waitingSecret:ticket?.secret||''})});
  const data=await response.json();
  if(response.status===409 && data?.code==='WAITING_HOST') throw new GuestWaitingForHost();
  if(response.status===409 && data?.code==='WAITING_APPROVAL') throw new GuestWaitingApproval(parseWaitingTicket(data));
  if(!response.ok||data?.success!==true) throw new Error('Invitația pentru sală este indisponibilă.');
  return data;
}

export function restoreSecureMeetingInvitation(storage: Pick<Storage, 'getItem'>): SecureMeetingInvitation | null {
  try {
    const saved = JSON.parse(storage.getItem(INVITE_KEY) || 'null');
    if (!saved || !/^meet-[a-f0-9]{32}$/.test(saved.meetingId) || !/^[A-Za-z0-9_-]{43}$/.test(saved.token)) return null;
    const encryptionKey = storedKey(saved.encryptionKey);
    return { meetingId: saved.meetingId, token: saved.token, ...(encryptionKey ? { encryptionKey } : {}) };
  } catch (_) { return null; }
}

export function newMeetingId(random: Crypto = crypto): string {
  const bytes = random.getRandomValues(new Uint8Array(16));
  return 'meet-' + Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
}

export function meetingInvitationUrl(meetingId: string, token: string): string {
  if (!/^meet-[a-f0-9]{32}$/.test(meetingId) || !/^[A-Za-z0-9_-]{43}$/.test(token)) throw new Error('Invitație invalidă.');
  const url = new URL('https://app.vorbello.eu/');
  url.searchParams.set('meeting', meetingId);
  // Fragments are not sent in HTTP requests or server access logs.
  url.hash = new URLSearchParams({ invitation: token }).toString();
  return url.toString();
}

export function setMeetingLicenseSession(value: string): void {
  if (!value || value.length > 8192) throw new Error('Sesiune de licență invalidă.');
  sessionStorage.setItem(SESSION_KEY, value);
}

export function clearMeetingLicenseSession(): void { sessionStorage.removeItem(SESSION_KEY); }

export async function requestRoomHost(roomId: string, displayName: string, transport: typeof fetch = fetch): Promise<{token:string; livekitUrl:string; mediaRoom:string}> {
  const session=sessionStorage.getItem(SESSION_KEY);
  if (!session || !/^room-[a-f0-9]{32}$/.test(roomId) || !displayName.trim() || displayName.length>160) throw new Error('Sală sau sesiune invalidă.');
  const response=await transport('https://app.vorbello.eu/room-host.php',{method:'POST',credentials:'omit',cache:'no-store',
    headers:{'Content-Type':'application/json',Authorization:`Bearer ${session}`},body:JSON.stringify({roomId,displayName})});
  const data=await response.json();
  if (!response.ok || data?.success!==true || typeof data.token!=='string' || !data.token ||
    typeof data.livekitUrl!=='string' || !/^wss:\/\/[a-z0-9.-]+(?::[0-9]+)?$/i.test(data.livekitUrl) ||
    typeof data.mediaRoom!=='string' || !/^room-[a-f0-9]{64}$/.test(data.mediaRoom)) throw new Error('Accesul la sală nu a fost autorizat.');
  return {token:data.token,livekitUrl:data.livekitUrl,mediaRoom:data.mediaRoom};
}

export async function requestWebinarPermission(meetingId: string, participantId: string, medium: 'camera' | 'microphone', allow: boolean, transport: typeof fetch = fetch): Promise<void> {
  const session = sessionStorage.getItem(SESSION_KEY);
  if (!session || !/^meet-[a-f0-9]{32}$/.test(meetingId) || !/^guest-[a-f0-9]{32}$/.test(participantId)) throw new Error('Sesiune sau participant neeligibil.');
  const response = await transport('https://app.vorbello.eu/webinar-permission.php', { method: 'POST', credentials: 'omit', cache: 'no-store',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session}` },
    body: JSON.stringify({ meetingId, participantId, medium, allow }) });
  const data = await response.json();
  if (!response.ok || data?.success !== true) throw new Error('Permisiunea nu a putut fi acordată.');
}

export class MeetingInviteClient {
  constructor(private readonly transport: typeof fetch = fetch, private readonly endpoint = 'https://app.vorbello.eu/meeting-invites.php') {}

  private async request(payload: Record<string, string>, authenticated: boolean): Promise<Record<string, unknown>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (authenticated) {
      const session = sessionStorage.getItem(SESSION_KEY);
      if (!session) throw new Error('Autentificați-vă cu un cont de licență pentru a gestiona invitațiile.');
      headers.Authorization = `Bearer ${session}`;
    }
    const transport = this.transport;
    const response = await transport(this.endpoint, { method: 'POST', headers, body: JSON.stringify(payload), cache: 'no-store', credentials: 'omit', referrerPolicy: 'no-referrer' });
    const result: unknown = await response.json();
    if (response.status === 409 && result && typeof result === 'object' && (result as Record<string, unknown>).code === 'WAITING_HOST') throw new GuestWaitingForHost(countdownStart(result));
    if (response.status === 409 && result && typeof result === 'object' && (result as Record<string, unknown>).code === 'WAITING_APPROVAL') throw new GuestWaitingApproval(parseWaitingTicket(result), countdownStart(result));
    if (response.status === 403 && result && typeof result === 'object' && (result as Record<string, unknown>).code === 'ORG_MEMBERS_ONLY') throw new Error('Această întâlnire este disponibilă doar membrilor organizației. Cere gazdei acces pentru invitați.');
    if (!response.ok || !result || typeof result !== 'object' || (result as Record<string, unknown>).success !== true) throw new Error('Invitația nu a putut fi verificată pe server.');
    return result as Record<string, unknown>;
  }

  async create(meetingId: string, startsAt: string, timezone: string): Promise<string> {
    const result = await this.request({ action: 'create', meetingId, startsAt, timezone }, true);
    if (typeof result.token !== 'string') throw new Error('Serverul nu a returnat invitația.');
    return result.token;
  }
  async get(meetingId: string): Promise<string> {
    const result = await this.request({ action: 'get', meetingId }, true);
    if (typeof result.token !== 'string') throw new Error('Serverul nu a returnat invitația.');
    return result.token;
  }
  async end(meetingId: string): Promise<void> { await this.request({ action: 'end', meetingId }, true); }
  async host(meetingId: string, displayName: string, e2eeBinding?: string): Promise<{token: string; livekitUrl: string; e2eeRequired: boolean}> {
    if (!/^meet-[a-f0-9]{32}$/.test(meetingId) || !displayName.trim() || displayName.length > 160) throw new Error('Întâlnire sau nume invalid.');
    if (e2eeBinding && !/^[a-f0-9]{64}$/.test(e2eeBinding)) throw new Error('Datele criptării sunt invalide.');
    const result = await this.request({action:'host', meetingId, displayName, ...(e2eeBinding ? {e2eeBinding} : {})}, true);
    if (typeof result.token !== 'string' || !result.token || typeof result.livekitUrl !== 'string' || !/^wss:\/\/[a-z0-9.-]+(?::[0-9]+)?$/i.test(result.livekitUrl)) throw new Error('Serverul nu a autorizat gazda.');
    if ((e2eeBinding && result.e2eeRequired !== true) || (!e2eeBinding && result.e2eeRequired === true)) throw new Error('Politica de criptare nu a putut fi verificată.');
    return {token:result.token, livekitUrl:result.livekitUrl, e2eeRequired:result.e2eeRequired === true};
  }
  async join(meetingId: string, token: string, displayName: string, pin = '', ticket?:WaitingTicket, e2eeBinding?: string): Promise<{token: string; livekitUrl: string; isWebinar: boolean; e2eeRequired: boolean; countdownStartsAt?: string}> {
    if (!/^meet-[a-f0-9]{32}$/.test(meetingId) || !/^[A-Za-z0-9_-]{43}$/.test(token) || !displayName.trim() || displayName.length > 160) throw new Error('Invitație sau nume invalid.');
    if (e2eeBinding && !/^[a-f0-9]{64}$/.test(e2eeBinding)) throw new Error('Datele criptării sunt invalide.');
    const result = await this.request({ action: 'join', meetingId, token, displayName, pin, waitingId:ticket?.id||'', waitingSecret:ticket?.secret||'', ...(e2eeBinding ? {e2eeBinding} : {}) }, false);
    if (typeof result.token !== 'string' || !result.token || typeof result.livekitUrl !== 'string' || !/^wss:\/\/[a-z0-9.-]+(?::[0-9]+)?$/i.test(result.livekitUrl) || typeof result.isWebinar !== 'boolean') throw new Error('Serverul nu a returnat accesul video.');
    if ((e2eeBinding && result.e2eeRequired !== true) || (!e2eeBinding && result.e2eeRequired === true)) throw new Error('Politica de criptare nu a putut fi verificată.');
    return { token: result.token, livekitUrl: result.livekitUrl, isWebinar: result.isWebinar, e2eeRequired:result.e2eeRequired === true, countdownStartsAt:countdownStart(result) };
  }
}
